import { invert, isArray, toLower } from 'lodash';
import API from 'services/API';
import { moment } from 'utils';
import taxesTitleMap from '../../constants/taxesTitleMap';

export default function getTaxes(params) {
  return API.request({
    method: 'GET',
    url: '/taxpayer/krsbs/v2',
    params,
    mapper: mapData,
  });
}

const titleTaxMap = invert(taxesTitleMap);

export function mapData(data) {
  return data.filter(isKrsbNotEmpty).map(function (krsb) {
    const tax = {
      id: krsb.krsbId,
      type: krsb.krsbType,
      oktmo: krsb.oktmo,
      kbk: krsb.kbk,
      oktmoName: krsb.oktmoName,
      objects: mapObjects(krsb),
      ...mapDebt(krsb),
      taxType: mapTaxType(krsb),
      taxAuthority: mapTaxAuthority(krsb),
      objectsNumber: getKrsbObjectsNumber(krsb),
    };

    tax.kbkType = getKbkType(tax.taxType);

    return tax;
  });
}

function getKbkType(taxType) {
  return titleTaxMap[taxType.title?.toLowerCase()] || null;
}

function getKrsbObjectsNumber(krsb) {
  return isArray(krsb.accruals) ? krsb.accruals.length : 0;
}

function mapDebt({ taxBalance, fineBalance, penaltyBalance, percentBalance, arrears }) {
  return {
    debt: taxBalance,
    fine: fineBalance,
    penalty: penaltyBalance || 0,
    percent: percentBalance || 0,
    arrears,
  };
}

function mapTaxAuthority({ taxAuthority }) {
  return {
    code: taxAuthority.code,
    title: taxAuthority.name,
    shortTitle: taxAuthority.code,
  };
}

export function mapTaxType({ kbk: taxType, payments }) {
  let title;

  switch (toLower(taxType.name)) {
    case 'налог на доходы физических лиц':
      title = 'налог на доходы';
      break;
    case 'на доходы в виде процентов по вкладам': // TODO уточнить сопоставление
      title = 'на доходы в виде процентов по вкладам';
      break;
    case 'на доходы свыше 5 млн руб': // TODO уточнить сопоставление
      title = 'на доходы свыше 5 млн руб';
      break;
    case 'на другие доходы': // TODO уточнить сопоставление
      title = 'на иные виды доходов';
      break;
    case 'налог на имущество физических лиц':
      title = 'налог на имущество';
      break;
    case 'единый налоговый платеж физического лица':
      title = 'единый налоговый платеж';
      break;
    case 'прочие платежи и сборы, по которым крсб не ведутся':
      title = 'административный штраф';
      break;
    case 'штрафы, которые невозможно отнести к конкретному налогу':
      title = 'административный штраф';
      break;
    default:
      title = taxType.name;
  }

  return {
    id: taxType.codeKbkId,
    title: title,
    code: taxType.code,
    kbkSortWight: taxType.codeKbkId,
    payments: mapPayments(payments),
  };
}

function mapObjects({ accruals }) {
  const res = {};
  Object.entries(accruals || {}).forEach(([id, values]) => {
    res[id] = {
      id,
      values: values.sort((v1, v2) => v2.year - v1.year),
    };
  });
  return res;
}

function isKrsbNotEmpty(krsb) {
  const debtsNotEmpty =
    krsb.taxBalance !== 0 ||
    krsb.fineBalance !== 0 ||
    krsb.penaltyBalance !== 0 ||
    krsb.percentBalance;
  const payments = (krsb.payments || []).filter((payment) => payment.sum);
  return (
    debtsNotEmpty ||
    payments.length ||
    Object.values(krsb.accruals).reduce(function (prev, object) {
      return prev || object.reduce((sum, o) => sum + o.value, 0) > 0;
    }, false)
  );
}

function mapPayments(payments = []) {
  const totals = payments.reduce(
    (total, payment) => {
      if (!payment.bankConfirm) {
        total.completed += payment.sum;
      } else {
        total.bankConfirm += payment.sum;
      }
      return total;
    },
    {
      bankConfirm: 0,
      completed: 0,
    },
  );

  return {
    total: +(totals.bankConfirm + totals.completed).toFixed(2),
    bankConfirmTotal: totals.bankConfirm,
    completedTotal: totals.completed,
    detail: payments
      .sort((p1, p2) => new Date(p2.date) - new Date(p1.date))
      .map(function (payment) {
        const date = moment.fromServerDatetime(payment.date);
        return {
          title: `${date.format('DD.MM.YYYY')}`,
          sum: payment.sum,
          date,
          bankConfirm: payment.bankConfirm,
          paymentType: payment.bankConfirm ? payment.paymentKind : null,
          paymentSource: payment.paymentSource,
        };
      }),
  };
}
